<template>
  <div
    v-if="profile.featureFlags.adaptive && blockCount > 0"
    ref="banner"
    class="recommendation-banner"
    :class="[`items-${blockCount}`, { visible: visible }]"
  >
    <div ref="solid" class="solid" />
    <div class="recommendation-banner-content">
      <h2 ref="titleElement" class="title">
        {{ title }}
      </h2>

      <div ref="recoElements" class="recommendation-banner-content-blocks">
        <RecommendationBlockErrors
          v-if="exercisesWithErrors.length"
          ref="recoErrors"
          :ready="progressErrors === 1"
          class="errors"
        />
        <RecommendationBlockContents
          v-if="recommendedItems.length"
          ref="recoContents"
          :ready="progressContents === 1"
          class="contents"
        />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { gsap } from 'gsap';

const visible = ref<boolean>(false);

const banner = ref(null);

const recoErrors = ref(null);

const titleElement = ref(null);

const solid = ref(null);

const recommendation = useRecommendation();

const profile = useProfileStore();

const { categoryManager } = inject(INJECT_CATEGORY_PAGE);

provide(INJECT_RECOMMENDATION, recommendation);

const {
  exercisesWithErrors,
  recommendedItems,
  score,
  mScoresForRecommendedItems,
} = recommendation;

const progressErrors = ref<number>(0);

const progressContents = ref<number>(0);

const blockCount = computed(() => {
  let count = 0;
  count += exercisesWithErrors.value.length ? 1 : 0;
  count += recommendedItems.value.length ? 1 : 0;
  return count;
});

const title = computed(() =>
  score.value >= 0.6
    ? `Superbe travail ! Et si tu allais encore plus loin`
    : `Ne lâche pas maintenant !
Avec un peu d’entraînement, ça va venir !`
);

const { showRecommendations } =
  useAppEventChapterRecommendation(categoryManager);

const isMounted = useMounted();

let timeline;

const start = async () => {
  await recommendation.load();
  if (!isMounted.value) return;
  // animation
  timeline = gsap.timeline({ delay: 0 });
  timeline.fromTo(
    solid.value,
    { scaleY: 0 },
    {
      scaleY: 1,
      duration: 0.8,
      ease: 'elastic.out(1,0.9)',
    },
    0
  );

  timeline.fromTo(
    titleElement.value,
    { y: 50 },
    {
      y: 0,
      duration: 0.7,
      ease: 'elastic.out(1,0.9)',
    },
    0.1
  );

  timeline.fromTo(progressErrors, { value: 0 }, { value: 1, duration: 0.1 }, 0);

  timeline.fromTo(
    progressContents,
    { value: 0 },
    { value: 1, duration: 0.12 },
    0
  );
  // end animation

  showRecommendations(
    exercisesWithErrors.value.map((item) => item.exercise.id),
    recommendedItems.value.map((item) => ({
      id: item.id,
      type: item.type,
      recommendationScore: mScoresForRecommendedItems.value.has(item.id)
        ? mScoresForRecommendedItems.value.get(item.id)
        : 0,
    }))
  );
};

onMounted(() => {
  start();
});

onUnmounted(() => {
  if (timeline) timeline.kill();
});
</script>
<style lang="scss" scoped>
.recommendation-banner {
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
  padding: var(--gap_l) var(--gap_n);

  .solid {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    transform: scaleY(0);
    transform-origin: 50% 100%;
    background-color: var(--color_secondary_darker);
  }

  @include desktop {
    padding: var(--gap_xl) var(--row_gap);
  }
  &-content {
    display: flex;
    z-index: 1;
    flex-direction: column;
    width: 100%;
    overflow: hidden;
    gap: var(--gap_m);
    @include above('l') {
      width: 69.6rem;
    }
  }
  &.items-2,
  &.items-1 {
    .recommendation-banner-content {
      &-blocks {
        display: grid;
        grid-auto-flow: row;
        grid-template-areas: 'errors' 'contents';
        grid-template-columns: auto;
        grid-template-rows: auto auto;
        gap: var(--gap_m);
        > * {
          min-width: 0;
        }
        @include desktop {
          display: grid;
          gap: var(--gap_xl);
          grid-template-areas: 'errors contents';
          grid-template-columns: 40% auto;
          grid-template-rows: auto;
        }
      }
    }
  }
  &.items-2 {
    .recommendation-banner-content {
      @include desktop {
        width: calc(var(--main_width));
      }
    }
  }
  &.items-1 {
    .recommendation-banner-content {
      &-blocks {
        grid-template-areas: 'single';
        grid-template-rows: auto;

        @include desktop {
          grid-template-areas: 'single';
          grid-template-columns: auto;
          grid-template-rows: auto;
        }
      }
    }
  }
}
.title {
  @include font('l', 800, 'secondary');

  color: var(--color_neutral_25);
  text-align: left;
}
.contents {
  grid-area: contents;
}
.errors {
  grid-area: errors;
}
.items-1 {
  .contents,
  .errors {
    grid-area: single;
    justify-self: center;
  }
}
</style>
